import React, { useEffect, useReducer, useState } from 'react';
import { Box, Button, Chip, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { parseSqlAllowedIps } from './sqlAllowedIps';
import { useTranslation } from 'react-i18next';
import { fmtDate } from 'pages/console/bi-api/format';

export { createSqlAllowedIpsInput, parseSqlAllowedIps } from './sqlAllowedIps';

export function SqlAllowedIpsField({ value, onChange, disabled, error, clientIp = null }) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState(false);
  let currentIp = null;
  try { currentIp = parseSqlAllowedIps({ addresses: [clientIp], draft: '' })[0]; } catch (_) { /* No trustworthy public IPv4 suggestion. */ }
  const currentAddress = currentIp?.replace(/\/32$/, '');
  const alreadyAdded = currentAddress && value.addresses.some((ip) => ip.replace(/\/32$/, '') === currentAddress);
  const addCurrentIp = () => {
    if (disabled || !currentIp || alreadyAdded || value.addresses.length >= 16) return;
    try {
      const addresses = parseSqlAllowedIps({ addresses: [...value.addresses, currentIp], draft: '' });
      onChange({ ...value, addresses });
    } catch (_) { setTouched(true); }
  };
  const message = (err) => t(err.code === 'limit' ? 'data.sqlAllowedIpsLimit' : 'data.sqlAllowedIpsInvalid');
  let validationError = null;
  try { parseSqlAllowedIps(value); } catch (err) { validationError = message(err); }
  const commit = (draft = value.draft) => {
    if (disabled) return;
    setTouched(true);
    try {
      const addresses = parseSqlAllowedIps({ addresses: value.addresses, draft });
      onChange({ addresses, draft: '' });
      setTouched(false);
    } catch (_) { onChange({ ...value, draft }); }
  };
  const change = (event) => {
    const draft = event.target.value;
    if (/[\s,]/.test(draft)) commit(draft);
    else onChange({ ...value, draft });
  };
  const fieldError = error || ((touched || !value.draft) && validationError);
  return (
    <Box sx={{ my: 2 }}>
      <TextField
        fullWidth
        label={t('data.sqlAllowedIps')}
        value={value.draft}
        onChange={change}
        onPaste={(event) => {
          const text = event.clipboardData.getData('text');
          if (!/[\s,]/.test(text)) return;
          event.preventDefault();
          const start = event.target.selectionStart ?? value.draft.length;
          const end = event.target.selectionEnd ?? start;
          commit(`${value.draft.slice(0, start)}${text}${value.draft.slice(end)}`.replace(/\s+/g, ' '));
        }}
        onBlur={() => { if (value.draft.trim()) commit(); }}
        onKeyDown={(event) => {
          if (!event.nativeEvent.isComposing && ['Enter', ',', ' '].includes(event.key)) {
            event.preventDefault();
            commit();
          }
        }}
        disabled={disabled}
        error={Boolean(fieldError)}
        helperText={fieldError || t('data.sqlAllowedIpsHelp')}
        placeholder={currentIp ? t('data.sqlAllowedIpCurrentExample', { ip: currentIp }) : t('data.sqlAllowedIpPlaceholder')}
        InputLabelProps={{ shrink: true, sx: { position: 'static', transform: 'none', mb: 1, fontSize: '0.8125rem', whiteSpace: 'normal', maxWidth: 'none' } }}
        InputProps={{
          notched: false,
          startAdornment: value.addresses.map((ip) => {
            let valid = true;
            try { parseSqlAllowedIps({ addresses: [ip], draft: '' }); } catch (_) { valid = false; }
            const label = ip.replace(/\/32$/, '');
            return <Chip key={ip} label={label} size="small" variant="outlined" color={valid ? 'default' : 'error'}
              aria-label={t('data.sqlAllowedIpRemove', { ip: label })}
              disabled={disabled} onDelete={() => onChange({ ...value, addresses: value.addresses.filter((item) => item !== ip) })} />;
          }),
          endAdornment: <Tooltip title={t('data.sqlAllowedIpAdd')}><span><IconButton aria-label={t('data.sqlAllowedIpAdd')} disabled={disabled || !value.draft.trim()} onMouseDown={(event) => event.preventDefault()} onClick={() => commit()} sx={{ width: 40, height: 40 }}><AddIcon fontSize="small" /></IconButton></span></Tooltip>,
          sx: {
            flexWrap: 'wrap', gap: 0.75, p: 1,
            '& input': { flex: 1, width: 'auto', minWidth: 120, p: 0.75 },
            '& .MuiOutlinedInput-notchedOutline': { top: 0, '& legend': { display: 'none' } },
          },
        }}
      />
      {currentAddress && !alreadyAdded && <Box sx={{ mt: 1 }}>
        <Tooltip title={disabled || value.addresses.length >= 16 ? '' : t('data.sqlCurrentIpHelp')} describeChild>
          <Button size="small" aria-label={t('data.sqlAddCurrentIp')} startIcon={<AddIcon />} disabled={disabled || value.addresses.length >= 16} onMouseDown={(event) => event.preventDefault()} onClick={addCurrentIp} sx={{ px: 0.75 }}>
            {t('data.sqlAddCurrentIp')}: <Box component="span" sx={{ ml: 0.75, fontFamily: 'var(--nb-mono)' }}>{currentAddress}</Box>
          </Button>
        </Tooltip>
      </Box>}
      {!currentAddress && <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>{t('data.sqlCurrentIpUnavailable')}</Typography>}
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{t('data.sqlIngressSharedNotice')}</Typography>
        <Tooltip title={`${t('data.sqlIngressSharedWarning')} ${t('data.sqlIngressClearWarning')}`} describeChild>
          <IconButton size="small" aria-label={t('data.sqlNetworkDetails')}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const ERROR_KEYS = {
  apply_failed: 'data.sqlNetworkApplyFailed',
  publish_failed: 'data.sqlNetworkPublishFailed',
  guard_failed: 'data.sqlNetworkGuardFailed',
  lease_expired: 'data.sqlNetworkLeaseExpired',
  invalid_policy: 'data.sqlNetworkInvalidPolicy',
};

/** Status is scoped to this connection's network revision, never the global ACK. */
export function SqlNetworkStatus({ connection }) {
  const { t } = useTranslation();
  const status = connection.network_status;
  const state = status?.state;
  const [, refreshExpiry] = useReducer((value) => value + 1, 0);
  const deadline = Date.parse(status?.valid_until);
  useEffect(() => {
    if (!Number.isFinite(deadline)) return undefined;
    const delay = deadline - Date.now();
    if (delay <= 0) return undefined;
    const timer = setTimeout(refreshExpiry, Math.min(delay + 10, 2147483647));
    return () => clearTimeout(timer);
  }, [deadline]);
  const expired = state === 'applied' && status.revision === connection.network_revision
    && (!Number.isFinite(deadline) || deadline <= Date.now());
  const applied = state === 'applied' && !expired && status.revision === connection.network_revision;
  const failed = state === 'error' || expired;
  return (
    <Box>
      <Typography sx={{ fontSize: '0.75rem', overflowWrap: 'anywhere' }}>
        {connection.sql_allowed_ips?.length ? connection.sql_allowed_ips.join(', ') : t('data.sqlNoAddressesAdded')}
      </Typography>
      <Chip
        size="small"
        color={applied ? 'success' : failed ? 'error' : 'warning'}
        variant="outlined"
        label={applied ? t('data.sqlNetworkApplied') : failed ? t('data.sqlNetworkError') : t('data.sqlNetworkPending')}
        sx={{ mt: 0.5 }}
      />
      {applied && status.applied_at && (
        <Typography variant="caption" display="block" color="text.secondary">{fmtDate(status.applied_at)}</Typography>
      )}
      {failed && (
        <Typography variant="caption" display="block" color="error">
          {t(expired ? 'data.sqlNetworkLeaseExpired' : ERROR_KEYS[status.error_code] || 'data.sqlNetworkApplyFailed')}
        </Typography>
      )}
    </Box>
  );
}
